/**
 * Validation utilities specifically for account management forms
 */

import { ValidationResult } from './validation';
import { containsProfanity } from './profanityFilter';

export interface AccountValidationRules {
  email: {
    pattern: RegExp;
    message: string;
  };
  username: {
    minLength: number;
    maxLength: number;
    pattern: RegExp;
    message: string;
  };
  password: {
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumber: boolean;
    message: string;
  };
}

export const ACCOUNT_VALIDATION_RULES: AccountValidationRules = {
  email: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Please enter a valid email address'
  },
  username: {
    minLength: 3,
    maxLength: 30,
    pattern: /^[a-zA-Z0-9_-]+$/,
    message: 'Username can only contain letters, numbers, underscores, and hyphens'
  },
  password: {
    minLength: 6,
    requireUppercase: true,
    requireLowercase: true,
    requireNumber: true,
    message: 'Password must be at least 6 characters with 1 uppercase, 1 lowercase, and 1 number'
  }
};

/**
 * Validates email format
 */
export const validateEmail = (email: string): ValidationResult => {
  const trimmedEmail = email.trim();
  
  if (!trimmedEmail) {
    return {
      isValid: false,
      error: 'Email is required'
    };
  }

  if (!ACCOUNT_VALIDATION_RULES.email.pattern.test(trimmedEmail)) {
    return {
      isValid: false,
      error: ACCOUNT_VALIDATION_RULES.email.message
    };
  }

  return { isValid: true };
};

/**
 * Validates username format and requirements
 */
export const validateUsername = (username: string): ValidationResult => {
  const trimmedUsername = username.trim();
  
  if (!trimmedUsername) {
    return {
      isValid: false,
      error: 'Username is required'
    };
  }

  const { minLength, maxLength, pattern, message } = ACCOUNT_VALIDATION_RULES.username;

  if (trimmedUsername.length < minLength) {
    return {
      isValid: false,
      error: `Username must be at least ${minLength} characters long`
    };
  }

  if (trimmedUsername.length > maxLength) {
    return {
      isValid: false,
      error: `Username cannot exceed ${maxLength} characters`
    };
  }

  if (!pattern.test(trimmedUsername)) {
    return {
      isValid: false,
      error: message
    };
  }

  if (containsProfanity(trimmedUsername)) {
    return {
      isValid: false,
      error: 'Username contains inappropriate language'
    };
  }

  return { isValid: true };
};

/**
 * Validates password strength
 */
export const validatePassword = (password: string): ValidationResult => {
  if (!password) {
    return {
      isValid: false,
      error: 'Password is required'
    };
  }

  const { minLength, requireUppercase, requireLowercase, requireNumber } = ACCOUNT_VALIDATION_RULES.password;

  if (password.length < minLength) {
    return {
      isValid: false,
      error: `Password must be at least ${minLength} characters long`
    };
  }

  if (requireUppercase && !/[A-Z]/.test(password)) {
    return {
      isValid: false,
      error: 'Password must contain at least one uppercase letter'
    };
  }

  if (requireLowercase && !/[a-z]/.test(password)) {
    return {
      isValid: false,
      error: 'Password must contain at least one lowercase letter'
    };
  }

  if (requireNumber && !/\d/.test(password)) {
    return {
      isValid: false,
      error: 'Password must contain at least one number'
    };
  }

  return { isValid: true };
};

/**
 * Validates password confirmation matches
 */
export const validatePasswordConfirmation = (password: string, confirmation: string): ValidationResult => {
  if (!confirmation) {
    return {
      isValid: false,
      error: 'Password confirmation is required'
    };
  }

  if (password !== confirmation) {
    return {
      isValid: false,
      error: 'Passwords do not match'
    };
  }

  return { isValid: true };
};

/**
 * Gets password strength score (0-4)
 */
export const getPasswordStrength = (password: string): {
  score: number;
  label: string;
  color: string;
} => {
  let score = 0;
  
  if (password.length >= 6) score++;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  // Normalize to 0-4 scale
  const normalizedScore = Math.min(4, Math.floor(score / 1.5));

  const strengthMap = {
    0: { label: 'Very Weak', color: 'text-red-600' },
    1: { label: 'Weak', color: 'text-red-500' },
    2: { label: 'Fair', color: 'text-yellow-500' },
    3: { label: 'Good', color: 'text-blue-500' },
    4: { label: 'Strong', color: 'text-green-500' }
  };

  return {
    score: normalizedScore,
    ...strengthMap[normalizedScore as keyof typeof strengthMap]
  };
};